import { theme } from 'antd';
import {
  THEME_COLOR,
  DARK_MODE,
  GRAY_SCALE,
  BORDER,
  BODY_BACKGROUND,
  DARK_PALETTE,
} from 'constants/ThemeConstant';

export function rgba(hex, opacity = 1) {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    throw new Error('Invalid Hex');
  }

  const decimal = parseInt(hex.substring(1), 16);

  const r = (decimal >> 16) & 255;
  const g = (decimal >> 8) & 255;
  const b = decimal & 255;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Mezcla dos colores hex: ratio 1 devuelve `hex`, ratio 0 devuelve `base`
const mixHex = (hex, base, ratio) => {
  const toRgb = (h) => [1, 3, 5].map((i) => parseInt(h.substr(i, 2), 16));
  const [a, b] = [toRgb(hex), toRgb(base)];
  return `#${a
    .map((c, i) =>
      Math.round(c * ratio + b[i] * (1 - ratio))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
};

// Sobrescribe la paleta que antd usa en Tag, Badge, etc. (color="red", ...)
// para que en oscuro no queden tonos saturados.
const getDarkPresetTokens = () => {
  const palette = { ...DARK_PALETTE, pink: DARK_PALETTE.magenta };
  return Object.entries(palette).reduce((acc, [name, { solid, bright }]) => {
    // Tags invertidos: fondo profundo del tono y texto brillante
    const shades = {
      1: mixHex(solid, DARK_MODE.BG_COLOR, 0.38),
      2: mixHex(solid, DARK_MODE.BG_COLOR, 0.5),
      3: mixHex(solid, DARK_MODE.BG_COLOR, 0.6),
      6: solid,
      7: bright,
    };
    Object.entries(shades).forEach(([i, color]) => {
      acc[`${name}${i}`] = color;
      acc[`${name}-${i}`] = color;
    });
    return acc;
  }, {});
};

export const baseTheme = {
  borderRadius: 10,
  colorPrimary: THEME_COLOR.BLUE,
  colorSuccess: THEME_COLOR.CYAN,
  colorWarning: THEME_COLOR.GOLD,
  colorError: THEME_COLOR.VOLCANO,
  colorInfo: THEME_COLOR.BLUE,
  colorText: GRAY_SCALE.GRAY,
  colorBorder: BORDER.BASE_COLOR,
  colorBgBody: BODY_BACKGROUND,
  controlHeight: 40,
  controlHeightLG: 48,
  controlHeightSM: 36,
  fontFamily: `'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji'`,
  fontSizeHeading2: 22,
  fontSizeHeading4: 17,
};

const getBaseComponentThemeConfig = ({
  Button = {},
  Menu = {},
  Card = {},
  Table = {},
  Select = {},
  DatePicker = {},
  Layout = {},
  Input = {},
  Dropdown = {},
  Calendar = {},
} = {}) => {
  return {
    Button: {
      ...Button,
    },
    Dropdown: {
      controlPaddingHorizontal: 20,
      controlHeight: 37,
      borderRadiusLG: 10,
      paddingXXS: '4px 0px',
      ...Dropdown,
    },
    Calendar: {
      ...Calendar,
    },
    Checkbox: {
      lineWidth: 2,
      borderRadiusSM: 4,
    },
    Card: {
      colorTextHeading: GRAY_SCALE.GRAY_DARK,
      paddingLG: 20,
      ...Card,
    },
    Layout: {
      colorBgBody: BODY_BACKGROUND,
      colorBgHeader: GRAY_SCALE.WHITE,
      ...Layout,
    },
    Breadcrumb: {
      colorTextDescription: GRAY_SCALE.GRAY_LIGHT,
      colorText: baseTheme.colorPrimary,
      colorBgTextHover: 'transparent',
    },
    Menu: {
      colorItemBg: 'transparent',
      colorActiveBarHeight: 0,
      colorActiveBarWidth: 3,
      colorItemTextSelectedHorizontal: baseTheme.colorPrimary,
      colorItemTextHover: baseTheme.colorPrimary,
      colorItemTextSelected: baseTheme.colorPrimary,
      colorItemBgSelected: rgba(baseTheme.colorPrimary, 0.1),
      colorItemBgHover: 'transparent',
      radiusItem: 0,
      colorSubItemBg: 'transparent',
      itemMarginInline: 0,
      controlHeightLG: 40,
      controlHeightSM: 22,
      ...Menu,
    },
    Input: {
      ...Input,
    },
    Pagination: {
      paginationItemSize: 30,
      borderRadius: '50%',
      colorBgContainer: baseTheme.colorPrimary,
      colorPrimary: GRAY_SCALE.WHITE,
      colorPrimaryHover: GRAY_SCALE.WHITE,
    },
    Steps: {
      wireframe: true,
      controlHeight: 32,
      waitIconColor: GRAY_SCALE.GRAY_LIGHT,
    },
    DatePicker: {
      controlHeight: 40,
      controlHeightSM: 26,
      borderRadiusSM: 6,
      lineWidthBold: 0,
      ...DatePicker,
    },
    Radio: {
      fontSizeLG: 18,
    },
    Slider: {
      colorPrimaryBorder: rgba(baseTheme.colorPrimary, 0.8),
      colorPrimaryBorderHover: baseTheme.colorPrimary,
    },
    Select: {
      paddingXXS: '4px 0px',
      controlHeight: 40,
      controlHeightSM: 30,
      controlItemBgActive: rgba(baseTheme.colorPrimary, 0.1),
      ...Select,
    },
    TreeSelect: {
      controlHeightSM: 24,
    },
    Tooltip: {
      colorBgDefault: rgba(GRAY_SCALE.DARK, 0.75),
      controlHeight: 32,
    },
    Timeline: {
      lineType: 'dashed',
    },
    Tag: {
      lineHeight: 2.1,
    },
    Table: {
      colorText: GRAY_SCALE.GRAY,
      tableHeaderBg: 'transparent',
      ...Table,
    },
  };
};

export const lightTheme = {
  token: {
    ...baseTheme,
    colorTextBase: GRAY_SCALE.GRAY,
  },
  components: getBaseComponentThemeConfig(),
};

export const darkTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...baseTheme,
    ...getDarkPresetTokens(),
    colorPrimary: DARK_MODE.PRIMARY_COLOR,
    colorInfo: DARK_MODE.PRIMARY_COLOR,
    colorSuccess: DARK_PALETTE.green.solid,
    colorWarning: DARK_PALETTE.gold.solid,
    colorError: DARK_PALETTE.red.solid,
    colorTextBase: DARK_MODE.TEXT_COLOR,
    colorBgBase: DARK_MODE.BG_COLOR,
    colorBgBody: DARK_MODE.BODY_BG_COLOR,
    colorBgLayout: DARK_MODE.BODY_BG_COLOR,
    colorBgContainer: DARK_MODE.BG_COLOR,
    colorBgElevated: DARK_MODE.ELEVATED_BG_COLOR,
    colorBorder: DARK_MODE.BORDER_BASE_COLOR,
    colorBorderSecondary: DARK_MODE.BORDER_BASE_COLOR,
    colorText: DARK_MODE.TEXT_COLOR,
    colorTextHeading: DARK_MODE.HEADING_COLOR,
    colorTextPlaceholder: DARK_MODE.PLACEHOLDER_COLOR,
  },
  components: getBaseComponentThemeConfig({
    Button: {
      controlOutline: 'transparent',
      colorBgContainer: DARK_MODE.BUTTON_BG_COLOR,
      colorBorder: DARK_MODE.BUTTON_BORDER_COLOR,
      colorText: DARK_MODE.HEADING_COLOR,
    },
    Card: {
      colorTextHeading: DARK_MODE.HEADING_COLOR,
    },
    Layout: {
      colorBgBody: DARK_MODE.BODY_BG_COLOR,
      colorBgHeader: DARK_MODE.NAV_BG_COLOR,
    },
    Menu: {
      colorItemBgSelected: 'transparent',
      colorItemTextSelectedHorizontal: GRAY_SCALE.WHITE,
      colorItemTextHover: GRAY_SCALE.WHITE,
      colorItemTextSelected: GRAY_SCALE.WHITE,
      colorItemText: DARK_MODE.TEXT_COLOR,
      colorActiveBarWidth: 0,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Dropdown: {
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Calendar: {
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
    },
    Select: {
      controlOutline: 'transparent',
      colorBgContainer: DARK_MODE.ELEVATED_BG_COLOR,
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
    },
    Input: {
      controlOutline: 'transparent',
      colorBgContainer: DARK_MODE.ELEVATED_BG_COLOR,
    },
    DatePicker: {
      controlOutline: 'transparent',
      colorBgContainer: DARK_MODE.ELEVATED_BG_COLOR,
      boxShadowSecondary: DARK_MODE.DROP_DOWN_SHADOW,
      controlItemBgActive: DARK_MODE.ACTIVE_BG_COLOR,
      controlItemBgHover: DARK_MODE.HOVER_BG_COLOR,
    },
    Table: {
      colorText: DARK_MODE.TEXT_COLOR,
      colorTextHeading: DARK_MODE.HEADING_COLOR,
      tableHeaderBg: DARK_MODE.TABLE_HEADER_BG_COLOR,
    },
  }),
};
